import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { BarChart3, FileText, Clock, Users } from 'lucide-react';
import API from '../lib/api';

export default function AnalyticsPage() {
  const [timeData, setTimeData] = useState([]);
  const [docStats, setDocStats] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      // Get time records for last 7 days
      const today = new Date();
      const weekAgo = new Date(today);
      weekAgo.setDate(today.getDate() - 6);
      const startDate = weekAgo.toISOString().split('T')[0];
      const endDate = today.toISOString().split('T')[0];

      const [timeRes, docsRes] = await Promise.all([
        API.get(`/time-records?start_date=${startDate}&end_date=${endDate}`),
        API.get('/documents')
      ]);

      // Process time data by day
      const dayMap = {};
      const dayNames = ['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab'];
      for (let i = 0; i < 7; i++) {
        const d = new Date(weekAgo);
        d.setDate(weekAgo.getDate() + i);
        const key = d.toISOString().split('T')[0];
        dayMap[key] = { day: dayNames[d.getDay()], date: key, hours: 0, records: 0 };
      }

      (timeRes.data || []).forEach(r => {
        const dateKey = (r.clock_in || '').split('T')[0];
        if (dayMap[dateKey]) {
          dayMap[dateKey].records++;
          if (r.clock_out) {
            try {
              const diff = (new Date(r.clock_out) - new Date(r.clock_in)) / 3600000;
              dayMap[dateKey].hours += diff;
            } catch {}
          }
        }
      });
      setTimeData(Object.values(dayMap).map(d => ({ ...d, hours: Math.round(d.hours * 10) / 10 })));

      // Process document stats
      const docs = docsRes.data || [];
      const statusCount = { approved: 0, pending: 0, reviewed: 0, rejected: 0 };
      docs.forEach(d => {
        const status = d.validation_status || 'pending';
        if (statusCount[status] !== undefined) statusCount[status]++;
      });
      setDocStats([
        { name: 'Aprobados', value: statusCount.approved, color: '#FF6B35' },
        { name: 'Pendientes', value: statusCount.pending, color: '#FFAD85' },
        { name: 'Revisados', value: statusCount.reviewed, color: '#FFD4BA' },
        { name: 'Rechazados', value: statusCount.rejected, color: '#EF4444' },
      ].filter(d => d.value > 0));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-orange-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  const totalHours = timeData.reduce((acc, d) => acc + d.hours, 0);
  const totalRecords = timeData.reduce((acc, d) => acc + d.records, 0);
  const avgHours = totalRecords > 0 ? (totalHours / totalRecords).toFixed(1) : '0';

  return (
    <div className="space-y-6" data-testid="analytics-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Estadisticas</h1>
        <p className="text-gray-500 mt-1 text-sm">Resumen de actividad de los ultimos 7 dias</p>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white border border-orange-100 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
              <Clock className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Horas totales</p>
              <p className="text-2xl font-bold text-gray-900">{totalHours.toFixed(1)}h</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-orange-100 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-400 rounded-xl flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Fichajes</p>
              <p className="text-2xl font-bold text-gray-900">{totalRecords}</p>
            </div>
          </div>
        </div>
        <div className="bg-white border border-orange-100 rounded-xl p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-orange-600 rounded-xl flex items-center justify-center">
              <BarChart3 className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Media/fichaje</p>
              <p className="text-2xl font-bold text-gray-900">{avgHours}h</p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Bar chart - hours per day */}
        <div className="lg:col-span-2 bg-white border border-orange-100 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-4 text-sm">Horas trabajadas por dia</h3>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={timeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#FEE5D9" />
              <XAxis dataKey="day" fontSize={12} tick={{ fill: '#6B7280' }} />
              <YAxis fontSize={12} tick={{ fill: '#6B7280' }} />
              <Tooltip
                contentStyle={{ borderRadius: '12px', border: '1px solid #FFAD85', fontSize: '12px' }}
                labelStyle={{ color: '#374151', fontWeight: 600 }}
              />
              <Bar dataKey="hours" fill="#FF6B35" radius={[6, 6, 0, 0]} name="Horas" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie chart - document statuses */}
        <div className="bg-white border border-orange-100 rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-4 text-sm">Estado de documentos</h3>
          {docStats.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={180}>
                <PieChart>
                  <Pie data={docStats} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={40}>
                    {docStats.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-2 mt-2">
                {docStats.map(d => (
                  <div key={d.name} className="flex items-center justify-between text-xs">
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                      <span className="text-gray-600">{d.name}</span>
                    </div>
                    <span className="font-semibold text-gray-900">{d.value}</span>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center h-[180px]">
              <p className="text-sm text-gray-400">Sin documentos</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
